import { useState } from 'react'
import { useFloatingOffset } from '@/shared/hooks/use-floating-offset'
import { useLocation } from 'react-router-dom'
import { useNav } from '@/shared/hooks/use-nav'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { BrandMark } from '@/shared/components/brand-mark'
import { LEVEL_BANDS, type LevelBand } from '@/shared/levels'
import { overrideProfile, resetMatchState } from '@/shared/mock/data'
import { resetPlayerActions } from '@/shared/player-actions'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { userRepository } from '@/features/perfil/api/user-repository'
import { useSetMyProfileCache } from '@/features/perfil/use-my-profile'
import { buildOnboardingUserDoc } from './onboarding-doc'
import { track } from '@/core/analytics'
import { useAppReady } from '@/shared/hooks/use-app-ready'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { LEVEL_QUIZ, bandFromQuiz, type QuizAnswers } from '@/shared/level-quiz'


function registrationMethodOf(providerId: string | undefined) {
  if (providerId === 'google.com') return 'google_signin' as const
  if (providerId === 'phone') return 'phone' as const
  if (providerId === 'password') return 'email_password' as const
  return null
}

// First-run profile setup (post sign-up). Persists the declared profile to
// `users/{uid}` (levelSource 'self'), then hydrates the in-session mock store
// so the still-stubbed features speak with the same identity. Under the dev
// bypass there is no Firebase session — the mock store is the only write.
export function OnboardingPage() {
  // Publishes this screen's bottom chrome height so the update banner and
  // toasts float ABOVE it instead of covering the primary action.
  const setBottomChrome = useFloatingOffset()
  useAppReady()
  const navigate = useNav()
  const user = useAuthStore((s) => s.user)
  const setProfileCache = useSetMyProfileCache()
  // `phone` used to be prefilled from router state by the fake-OTP screen, which
  // was deleted on 2026-08-06 (it accepted ANY 6-digit code and then claimed
  // "Verificado por SMS ✓"). Nothing sets that state any more, so the prefill —
  // and the verified badge it drove — are gone with it. The `phoneVerified`
  // plumbing below stays: it writes `isPhoneValidated`/`phoneValidatedAt` and is
  // ready for the real OTP (roadmap #21).
  // `fromMatchId` is set only by the share door's "Crear mi perfil" — it is
  // what lets `onboarding_completed` distinguish a player the share loop
  // produced from one who found the app some other way.
  const navState = useLocation().state as { guestName?: string; fromMatchId?: string } | null
  // Deferred to Perfil (2026-08-06). The values still ride the onboarding doc so
  // its shape is unchanged; they are simply no longer asked for up front. Seven
  // questions before a player could see a single match, when only `name` ever
  // blocked submit — and two of them sat below the fold on a 390×844 phone.
  const phone = ''
  const [name, setName] = useState(navState?.guestName ?? '')
  // "No sé mi nivel" — opt-in, so onboarding stays two questions for everyone
  // who does know. See `level-quiz.ts` for why these three questions.
  const [quizOpen, setQuizOpen] = useState(false)
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswers>({})
  // Launch market prefill — editable, but Granada is right for ~everyone v1.
  const city = 'Granada'
  const [band, setBand] = useState<LevelBand>(LEVEL_BANDS[2]!) // 'Medio' — most common self-pick
  const position = 'Indiferente'
  // Asked at the moment it matters: joining a category match.
  const gender: 'Mujer' | 'Hombre' | null = null
  const homeClubId: string | null = null
  const [attempted, setAttempted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)

  async function onStart() {
    if (saving) return
    if (!name.trim()) {
      setAttempted(true)
      navigator.vibrate?.(20)
      return
    }
    navigator.vibrate?.(12)
    setSaving(true)
    setSaveError(false)

    // Persist first — the success path only runs once the profile is real
    // (truthful mutation confirms). Bypass mode has no Firebase session.
    if (!AUTH_BYPASS && user) {
      try {
        await userRepository.saveMine(
          user.uid,
          buildOnboardingUserDoc({
            name,
            city,
            phone,
            band,
            positionLabel: position,
            // Always false until real OTP ships — no code path can verify a
            // phone today, and claiming otherwise is what we just removed.
            phoneVerified: false,
            genderLabel: gender,
            homeClubId,
            email: user.email,
            photoURL: user.photoURL,
            registrationMethod: registrationMethodOf(user.providerData[0]?.providerId),
          }),
          { isNew: true },
        )
        const fresh = await userRepository.getMine(user.uid)
        setProfileCache(fresh)
      } catch {
        setSaving(false)
        setSaveError(true)
        navigator.vibrate?.(20)
        return
      }
    }

    track('onboarding_completed', {
      level: band.default,
      has_phone: Boolean(phone.trim()),
      from_guest: Boolean(navState?.fromMatchId),
    })
    // A NEW account starts clean: no inherited contacts, matches or overrides
    // from the seeded returning-player demo (cert-walk-8 first-run coherence).
    resetPlayerActions()
    resetMatchState()
    // The declared profile must be TRUE on Perfil (one fact, one store).
    overrideProfile({
      name: name.trim(),
      ...(city.trim() ? { city: city.trim() } : {}),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      level: band.default,
      position,
      matches: 0,
      streak: 0,
      noShows: 0,
    })
    void navigate('/partidos')
  }

  return (
    <main className="min-h-full flex flex-col px-6 py-5 safe-top safe-bottom">
      <div className="flex-1 w-full max-w-sm mx-auto flex flex-col gap-4 um-fade pb-2">
        <div className="flex flex-col items-center text-center gap-2">
          <span className="text-[color:var(--brand-mint)]"><BrandMark size={44} /></span>
          <h1 className="text-[length:var(--fs-xl)] font-bold">¿Cómo te llamas?</h1>
          <p className="text-[color:var(--fg-muted)]">Con esto ya puedes entrar en partidos.</p>
        </div>

        <label className="flex flex-col gap-2">
          <Label>Tu nombre</Label>
          <input
            className="field"
            placeholder="Nombre y apellidos"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {attempted && !name.trim() && (
            <span className="text-[length:var(--fs-xs)] text-[color:var(--bad)]">
              Dinos tu nombre para poder emparejarte.
            </span>
          )}
        </label>
        <div className="flex flex-col gap-2">
          <Label>
            Tu nivel{' '}
            <span className="font-normal text-[color:var(--fg-faint)]">— se ajusta solo al jugar</span>
          </Label>
          <div className="flex flex-wrap gap-2">
            {LEVEL_BANDS.map((b) => (
              <Chip key={b.key} active={b.key === band.key} onClick={() => setBand(b)}>{b.label}</Chip>
            ))}
          </div>
          {/* Six bare labels told a new player nothing — she could not tell
              "Medio" from "Medio alto", and the feed PRE-FILTERS on this, so a
              wrong guess quietly hides every match she could actually play.
              The description of the SELECTED band is always visible. */}
          <span className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
            {band.description}
          </span>
          <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
            Empiezas con nivel {band.default.toFixed(1)} — se ajusta solo con tus partidos.
          </span>
          {/* The escape hatch for somebody with no idea at all. Onboarding is
              two questions by decision, so this is OPT-IN: it costs a confident
              player one line of text they never tap. */}
          <button
            type="button"
            onClick={() => setQuizOpen(true)}
            className="self-start min-h-[var(--tap)] text-[length:var(--fs-sm)] font-semibold text-[color:var(--accent)] underline underline-offset-4"
          >
            No sé mi nivel — ayúdame
          </button>
        </div>
        <p className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
          Teléfono, posición, club y género los añades en Perfil cuando quieras. El género
          solo te lo pedimos si entras en un partido por categoría.
        </p>
      </div>

      <div ref={setBottomChrome} className="sticky bottom-0 w-full max-w-sm mx-auto pt-4 pb-1 bg-[var(--bg-deep)]">
        {saveError && (
          <p role="alert" className="text-[length:var(--fs-xs)] text-[color:var(--bad)] text-center pb-2">
            No pudimos guardar tu perfil. Revisa tu conexión e inténtalo de nuevo.
          </p>
        )}
        <Button size="lg" disabled={saving} aria-busy={saving} onClick={() => void onStart()}>
          {saving ? (
            <>
              <Loader2 size={18} className="motion-safe:animate-spin" /> Creando tu perfil…
            </>
          ) : (
            'Ver partidos en Granada'
          )}
        </Button>
      </div>

      <BottomSheet open={quizOpen} onClose={() => setQuizOpen(false)} title="¿Qué nivel tienes?">
        <div className="flex flex-col gap-4 pb-2">
          <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
            Tres preguntas. Nadie las ve, y puedes cambiar el nivel después.
          </p>

          {LEVEL_QUIZ.map((q) => (
            <div key={q.id} className="flex flex-col gap-2">
              <Label>{q.prompt}</Label>
              <div className="flex flex-col gap-2">
                {q.options.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setQuizAnswers((prev) => ({ ...prev, [q.id]: o.id }))}
                    aria-pressed={quizAnswers[q.id] === o.id}
                    className={`text-left min-h-[var(--tap)] px-3 py-2 rounded-[var(--radius)] border text-[length:var(--fs-sm)] ${
                      quizAnswers[q.id] === o.id
                        ? 'border-[var(--accent)] text-[color:var(--accent)] bg-[var(--accent-wash)]'
                        : 'border-[var(--line)] text-[color:var(--fg-muted)]'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* The result is SHOWN before it is applied — an assigned level the
              player cannot see the reasoning for is one they will not trust,
              and they are the ones who have to live with the matches it picks. */}
          {(() => {
            const suggested = bandFromQuiz(quizAnswers)
            if (!suggested) {
              return (
                <p className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
                  Contesta las tres para ver tu nivel.
                </p>
              )
            }
            return (
              <div className="flex flex-col gap-3 pt-1 border-t border-[var(--line)]">
                <div className="flex flex-col gap-1 pt-2">
                  <span className="font-semibold">Te pega {suggested.label}</span>
                  <span className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                    {suggested.description}
                  </span>
                </div>
                <Button
                  onClick={() => {
                    setBand(suggested)
                    setQuizOpen(false)
                  }}
                >
                  Usar {suggested.label}
                </Button>
              </div>
            )
          })()}
        </div>
      </BottomSheet>
    </main>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">{children}</span>
}
